import {
  EditContextWarning,
  RowCounts,
  StructuralDelta,
  VerifierArtifactIdentity,
} from '../write/writeTypes';

/**
 * The audit record (spec section 5.8, U5 section d.2 verbatim plus the verifier artifact identity).
 *
 * ADR-0008:59-66 — every engine write appends a JSONL record carrying the plan id, the before and
 * after digests, the returned row counts and `warnings[]`; the plan and the before-digest are
 * recorded BEFORE the page sends the request. A write with no record is a defect, not an exception.
 *
 * Two rules this file enforces in its shape rather than in prose:
 *
 * 1. **Whitelist projection.** A record is BUILT from named fields; no caller object is ever spread
 *    into it. That is why there is no free-form payload key. The planner has already refused any
 *    prohibited name in a request before a plan can reach the audit store (`plan.ts:73-89`,
 *    `:182-201`), and the preflight re-checks it independently (`write/prohibitedNames.ts`), so
 *    every string this file records has been scanned twice. Engine-authored strings (warnings, 422
 *    details) are recorded verbatim but capped.
 * 2. **No credential value is ever recorded** — no header, no token, no URL query string. The
 *    request is recorded as its method, its path and its size.
 */

/**
 * Where an entry stands.
 *
 * `unknown` is its own state and not a flavour of `failed`, because the two need opposite handling.
 * A `failed` entry is settled: the engine said what happened, nothing is owed. An `unknown` entry is
 * a request whose outcome nobody observed — a resolved 503, a reply with no row counts, a transport
 * error thrown after the body went out. The write may have landed. Until a human has read the
 * episode and exported the log, that uncertainty must block the next apply, so `unknown` is
 * UNRESOLVED (`auditStore.listUnresolved`) and is never cleared by `clearExported`. Filing it as
 * `failed` let the next session write straight over the top of it.
 */
export type AuditState = 'pending' | 'sent' | 'reconciled' | 'failed' | 'refused' | 'unknown' | 'settled';

/**
 * `settled` — the one way out of `unknown`, and it is a PERSON'S statement.
 *
 * S-3: nothing in this tree ever moved an `unknown` entry, so one dropped reply refused every
 * later apply in that browser for good, and the refusal told the writer to reload, which changes
 * nothing at all. The missing state is not a repair Basil can make: no code here can know what the
 * engine holds. What can be known is that a human went and read the episode and said so, and that
 * is what this state records — who attested, when, and which state they attested out of.
 *
 * It is NOT a success word and never becomes one. The entry keeps its response, its verdict and
 * its `outcomeKnown: false` exactly as they were written; the export still carries all of it. The
 * only thing that changes is that the uncertainty is no longer OWED, so it stops blocking.
 */
export interface AuditSettlement {
  /** The writer who attested, by the id the record's actor carries. */
  attestedBy: number | null;
  ts: string;
  /** The state the attestation was made out of, kept so the export still says what was in doubt. */
  from: AuditState;
}

export interface AuditDigestSet {
  v1: readonly number[] | null;
  v2: readonly number[] | null;
  layout: readonly number[] | null;
}

export interface AuditRecord {
  v: 1;
  /** key path part 1 (`[planId, chunkIndex]`). */
  planId: string;
  /** key path part 2. */
  chunkIndex: number;
  ts: string;
  state: AuditState;
  actor: { userId: number | null };
  story: {
    storyId: number;
    bookId: number | null;
    group: string | null;
    /** A1: the detected engine namespace, recorded in every entry. */
    environment: 'live' | 'test';
  };
  plan: {
    planIds: string[];
    writePath: 'edit-context' | 'whole-branch';
    countsMode: 'edit-context' | 'whole-branch';
    expectedCounts: RowCounts | null;
    expectedDelta: StructuralDelta | null;
  };
  request: {
    method: string;
    path: string;
    chunkCount: number;
    nodeCount: number;
    byteLength: number;
  };
  before: {
    serverTime: string | null;
    digests: AuditDigestSet | null;
    touchedNodes: string[];
  } | null;
  response: {
    status: number | null;
    counts: RowCounts | null;
    warnings: EditContextWarning[];
    details: Array<{ message: string }>;
    ms: number;
    /** false when the outcome of the request could not be observed. */
    outcomeKnown: boolean;
  } | null;
  after: {
    serverTime: string | null;
    digests: AuditDigestSet | null;
    /**
     * True when the verifier could not supply its canon of the after-read, so the digests are
     * ABSENT — not "there were none to record".
     *
     * BAR-SHIP-12: a `null` digest set on its own is indistinguishable from a clean close that
     * simply had nothing to write down, and the controller reported such a close as a success. The
     * flag is what makes the difference legible to whoever settles the entry, and it travels with
     * `state: 'unknown'`, which is what keeps the entry UNRESOLVED.
     */
    digestsAbsent: boolean;
  } | null;
  verdict: {
    ok: boolean;
    changedNodes: string[];
    unexpectedPaths: string[];
    countMismatch: { expected: RowCounts; actual: RowCounts } | null;
    deltaMismatch: { expected: StructuralDelta; actual: StructuralDelta } | null;
    verifierArtifact: VerifierArtifactIdentity | null;
  } | null;
  /** Set when a writer attested that they read the episode; null on every other entry. */
  settlement: AuditSettlement | null;
  /** Set when the entry records a refusal rather than a request. */
  refusal: string | null;
  /** A short, human-readable note; capped like every other free string. */
  message: string | null;
}

/** Engine-authored strings are recorded verbatim but never unbounded. */
export const AUDIT_STRING_CAP = 2000;

export function capString(value: unknown, cap: number = AUDIT_STRING_CAP): string {
  const text = typeof value === 'string' ? value : String(value);

  return text.length > cap ? `${text.slice(0, cap)}…` : text;
}

export interface NewAuditRecordArgs {
  planId: string;
  chunkIndex: number;
  chunkCount: number;
  planIds: string[];
  userId: number | null;
  storyId: number;
  bookId?: number | null;
  group?: string | null;
  environment: 'live' | 'test';
  writePath: 'edit-context' | 'whole-branch';
  countsMode: 'edit-context' | 'whole-branch';
  expectedCounts: RowCounts | null;
  expectedDelta?: StructuralDelta | null;
  method: string;
  path: string;
  nodeCount: number;
  byteLength: number;
  beforeServerTime?: string | null;
  beforeDigests?: AuditDigestSet | null;
  touchedNodes?: readonly string[];
  ts?: string;
}

/** The pre-write entry. It is written, and its transaction awaited, BEFORE the request is issued. */
export function newPendingRecord(args: NewAuditRecordArgs): AuditRecord {
  return {
    v: 1,
    planId: args.planId,
    chunkIndex: args.chunkIndex,
    ts: args.ts || new Date().toISOString(),
    state: 'pending',
    actor: { userId: args.userId },
    story: {
      storyId: args.storyId,
      bookId: args.bookId === undefined ? null : args.bookId,
      group: args.group === undefined ? null : args.group,
      environment: args.environment,
    },
    plan: {
      planIds: args.planIds.slice(),
      writePath: args.writePath,
      countsMode: args.countsMode,
      expectedCounts: args.expectedCounts,
      expectedDelta: args.expectedDelta === undefined ? null : args.expectedDelta,
    },
    request: {
      method: args.method,
      path: args.path,
      chunkCount: args.chunkCount,
      nodeCount: args.nodeCount,
      byteLength: args.byteLength,
    },
    before: {
      serverTime: args.beforeServerTime === undefined ? null : args.beforeServerTime,
      digests: args.beforeDigests === undefined ? null : args.beforeDigests,
      touchedNodes: (args.touchedNodes || []).slice(),
    },
    response: null,
    after: null,
    verdict: null,
    settlement: null,
    refusal: null,
    message: null,
  };
}

/**
 * Whether a writer's attestation can move this entry at all.
 *
 * The three states nobody has closed: `unknown`, where the outcome was not observed; `sent`, where
 * the body left and the entry was never closed; and `pending`, the pre-write entry.
 *
 * S-3b: `pending` used to be refused here, on the reasoning that an attestation about the episode
 * says nothing about a request whose own record has not been written yet. That is true of a pending
 * entry whose apply is STILL RUNNING, and false of an ABANDONED one. The entry is written and its
 * transaction awaited BEFORE the body is dispatched, so a page that dies after the request landed
 * leaves a pending entry that nothing will ever close: the writer's own control could not move it,
 * the guard went on refusing, and the only way out was a second, rarer version of the deadlock S-3
 * was about. A person can answer a pending entry for exactly the reason they can answer an unknown
 * one: they can go and read the episode, which is all anyone can do about either.
 *
 * This function is pure and knows nothing about what is in flight. Excluding an entry whose apply
 * is still running is the CALLER'S job, and `useApply.settleEarlier` does it by asking
 * `applyController.isActiveWrite`, on the key of the entry, before and inside the transition.
 *
 * `reconciled`, `failed`, `refused` and `settled` are already settled, so there is nothing to
 * attest to.
 */
export function canSettle(record: AuditRecord): boolean {
  return record.state === 'unknown' || record.state === 'sent' || record.state === 'pending';
}

export interface SettlementArgs {
  /** the writer making the statement. */
  attestedBy: number | null;
  ts?: string;
}

/**
 * The attestation transition: an unclosed entry becomes `settled`, and nothing else moves.
 *
 * Pure, like every other transition in this file. An entry it cannot move is returned AS IT WAS,
 * so a caller sweeping a set can apply it to all of them and write back only what changed.
 *
 * What it does NOT do is as load-bearing as what it does. It writes `state` and `settlement` and
 * touches nothing else, so an attested `pending` entry keeps its null `response`, its null `verdict`
 * and its null `after`: there is no evidence, the settlement says `from: 'pending'`, and the export
 * still shows an entry whose request was never answered. An attestation is a person's statement that
 * they read the episode. It is never a verified success and never becomes one.
 */
export function withSettlement(record: AuditRecord, args: SettlementArgs): AuditRecord {
  if (!canSettle(record)) {
    return record;
  }

  return {
    ...record,
    state: 'settled',
    settlement: {
      attestedBy: args.attestedBy,
      ts: args.ts || new Date().toISOString(),
      from: record.state,
    },
  };
}

/** A refusal entry: nothing was sent, and the reason is recorded by name. */
export function newRefusalRecord(args: NewAuditRecordArgs, refusal: string, message?: string): AuditRecord {
  const record = newPendingRecord(args);

  record.state = 'refused';
  record.refusal = refusal;
  record.message = message === undefined ? null : capString(message);

  return record;
}

export interface ResponsePatchArgs {
  status: number | null;
  counts: RowCounts | null;
  warnings?: readonly EditContextWarning[];
  details?: ReadonlyArray<{ message: string }>;
  ms: number;
  outcomeKnown: boolean;
  message?: string | null;
}

/**
 * The `sent` transition, written immediately on a response — including on a network failure.
 *
 * An outcome nobody observed becomes `unknown`, not `failed`: the request may have been applied,
 * and an entry that claims to be settled would let the next apply start over the top of it.
 */
export function withResponse(record: AuditRecord, patch: ResponsePatchArgs): AuditRecord {
  return {
    ...record,
    state: patch.outcomeKnown ? 'sent' : 'unknown',
    response: {
      status: patch.status,
      counts: patch.counts,
      warnings: (patch.warnings || []).map((warning) => ({
        nodeId: warning.nodeId,
        stepId: warning.stepId,
        answerId: warning.answerId,
        field: capString(warning.field, 200),
        message: capString(warning.message),
      })),
      details: (patch.details || []).map((detail) => ({ message: capString(detail.message) })),
      ms: patch.ms,
      outcomeKnown: patch.outcomeKnown,
    },
    message: patch.message === undefined || patch.message === null ? record.message : capString(patch.message),
  };
}

export interface VerdictPatchArgs {
  ok: boolean;
  changedNodes?: readonly string[];
  unexpectedPaths?: readonly string[];
  countMismatch?: { expected: RowCounts; actual: RowCounts } | null;
  deltaMismatch?: { expected: StructuralDelta; actual: StructuralDelta } | null;
  verifierArtifact: VerifierArtifactIdentity | null;
  afterServerTime?: string | null;
  afterDigests?: AuditDigestSet | null;
  /**
   * False when the write's own outcome was never observed — a resolved 503, a reply with no counts,
   * a transport error thrown after the body went out. The after-read and the verdict are still
   * taken and still recorded, because they are the only evidence of what the engine did; but the
   * entry closes as `unknown` — never `failed`, never `reconciled` — so it stays UNRESOLVED and the
   * next apply is refused until a human has settled it.
   */
  outcomeKnown?: boolean;
  /**
   * True when the verifier could not supply its canon of the after-read, so `afterDigests` is
   * absent rather than empty. Recorded on the entry so the absence is readable (BAR-SHIP-12).
   */
  afterDigestsAbsent?: boolean;
  /**
   * True when the write's own outcome WAS observed but the apply could not be verified — the
   * verifier was lost between the send and the close, or its canon of the after-read never
   * arrived. Nothing proved what the engine now holds, so the entry closes `unknown` for exactly
   * the reason an unobserved outcome does: it must stay UNRESOLVED and block the next apply until
   * a human has settled it.
   */
  unverified?: boolean;
  message?: string | null;
}

/** The closing transition, written after the full re-read and the verdict. */
export function withVerdict(record: AuditRecord, patch: VerdictPatchArgs): AuditRecord {
  return {
    ...record,
    state: patch.outcomeKnown === false || patch.unverified === true ? 'unknown' : 'reconciled',
    after: {
      serverTime: patch.afterServerTime === undefined ? null : patch.afterServerTime,
      digests: patch.afterDigests === undefined ? null : patch.afterDigests,
      digestsAbsent: patch.afterDigestsAbsent === true,
    },
    verdict: {
      ok: patch.ok,
      changedNodes: (patch.changedNodes || []).map((node) => capString(node, 64)),
      unexpectedPaths: (patch.unexpectedPaths || []).map((path) => capString(path, 200)),
      countMismatch: patch.countMismatch === undefined ? null : patch.countMismatch,
      deltaMismatch: patch.deltaMismatch === undefined ? null : patch.deltaMismatch,
      verifierArtifact: patch.verifierArtifact,
    },
    message: patch.message === undefined || patch.message === null ? record.message : capString(patch.message),
  };
}
